import {StyleSheet} from "aphrodite";
import * as React from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {LabeledTextField} from "@khanacademy/wonder-blocks-form";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslateYet} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";

import LicenseText from "./programs/license-text.tsx";

type Props = {
    onClose: () => unknown;
    // Returns true if the program was created successfully.
    onSave: (programName: string) => Promise<boolean>;
    isSaving: boolean;
};

export default function CreatePythonUserProgramModal({
    onClose,
    onSave,
    isSaving,
}: Props): React.ReactElement {
    const [didMutationFail, setDidMutationFail] = React.useState(false);
    const [programName, setProgramName] = React.useState("");

    const isProgramNameInvalid = programName.trim() === "";

    return (
        <ModalLauncher
            opened={true}
            onClose={onClose}
            modal={({closeModal}) => (
                <OnePaneDialog
                    style={styles.dialog}
                    title={doNotTranslateYet`Save as...`}
                    content={
                        <View style={styles.content}>
                            <View style={styles.banner}>
                                <Banner
                                    text={doNotTranslateYet`This program will be publicly visible.`}
                                />
                            </View>
                            <LabeledTextField
                                label={
                                    <LabelLarge>
                                        {doNotTranslateYet`What would you like to name your new program?`}
                                    </LabelLarge>
                                }
                                placeholder={doNotTranslateYet`Program name`}
                                value={programName}
                                onChange={setProgramName}
                                disabled={isSaving}
                                style={styles.programNameInput}
                            />
                            <Body>
                                {doNotTranslateYet`To find your programs later, you can click “My Projects” on your Learner homepage.`}
                            </Body>
                            <LicenseText />
                            {didMutationFail && (
                                <Body role="alert" style={styles.errorMessage}>
                                    {doNotTranslateYet`We could not create your new program. Please try again.`}
                                </Body>
                            )}
                        </View>
                    }
                    footer={
                        <View style={styles.footer}>
                            <Button
                                kind="tertiary"
                                onClick={onClose}
                                disabled={isSaving}
                            >
                                {doNotTranslateYet`Cancel`}
                            </Button>
                            <Button
                                onClick={async () => {
                                    setDidMutationFail(false);
                                    const result = await onSave(programName);
                                    setDidMutationFail(!result);
                                }}
                                disabled={isProgramNameInvalid}
                                spinner={isSaving}
                            >
                                {doNotTranslateYet`Save`}
                            </Button>
                        </View>
                    }
                />
            )}
        />
    );
}

const styles = StyleSheet.create({
    dialog: {
        [mediaQueries.mdOrLarger]: {
            maxWidth: 617,
            maxHeight: 525,
        },
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: spacing.small_12,
    },
    banner: {
        paddingBottom: spacing.small_12,
    },
    programNameInput: {
        marginBottom: spacing.small_12,
    },
    errorMessage: {
        color: color.red,
    },
    footer: {
        display: "flex",
        flexDirection: "row",
        gap: spacing.medium_16,
    },
});
